import { useEffect, useMemo, useState } from 'react';
import {
  useOrganizations,
  useInviteTokenInfo,
} from '@/hooks/api/useOrganizations';
import {
  getPersistedInviteToken,
  clearPersistedInviteToken,
} from '@/lib/inviteTokenPersistence';
import { isTerminalInviteError } from '@/lib/inviteErrors';
import { safeSessionStorage } from '@/lib/safeSessionStorage';
import { InviteAcceptModal } from './InviteAcceptModal';

/**
 * Surfaces the organization invitation approve modal when the user arrives from
 * an invite email link — see KIR-3198 and KIR-3786.
 *
 * The invite link carries an opaque `?invite_token=<token>` (KIR-3786). That
 * token — not the typed email or Descope identity — is the source of truth for
 * which org the user joins: we resolve it server-side and, on accept, add the
 * currently authenticated user to that org. This fixes invited users landing in
 * a personal account when their registered identity differs from the one the
 * invite was created for.
 *
 * The token is captured once on mount. It falls back to the value persisted by
 * `captureInviteTokenFromUrl` (App entry) because a first-time invitee's signup
 * redirect returns to the app without the query param, and the post-login
 * landing redirect (ProfileRestoration) also navigates away from `/` and drops
 * it — either way the persisted copy keeps the invite alive (KIR-3786).
 *
 * Legacy `?invite=<orgId>` links (KIR-3198) still work: they match the org in the
 * user's pending-invite list and open the same modal.
 */
export function InviteGuard() {
  const [inviteToken] = useState<string | null>(() =>
    getPersistedInviteToken()
  );
  const [legacyInviteOrgId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('invite')
  );
  const [dismissed, setDismissed] = useState(false);
  // Org ids whose no-deep-link prompt was closed this session. Per-org rather
  // than a single boolean so closing one invite doesn't hide a second one.
  const [autoDismissedOrgIds, setAutoDismissedOrgIds] = useState<string[]>(
    () => safeSessionStorage.getItem('dismissedInviteOrgIds') ?? []
  );

  const dismissAutoInvite = (organizationId: string): void => {
    const next = Array.from(new Set([...autoDismissedOrgIds, organizationId]));
    setAutoDismissedOrgIds(next);
    safeSessionStorage.setItem('dismissedInviteOrgIds', next);
  };

  const {
    data: tokenInfo,
    error: tokenInfoError,
    isPending: tokenQueryPending,
  } = useInviteTokenInfo(inviteToken);
  const { data: organizations = [] } = useOrganizations();

  // Drop the persisted token ONLY when the invite is terminally dead — gone
  // (404), or a token that can never resolve (400). Transient failures
  // (network, 5xx) must NOT clear it, or a single blip after signup would drop
  // the invite on the next bare-origin load — the exact KIR-3786 failure this
  // persistence guards against (useInviteTokenInfo has retry disabled).
  useEffect(() => {
    if (inviteToken && isTerminalInviteError(tokenInfoError)) {
      clearPersistedInviteToken();
    }
  }, [inviteToken, tokenInfoError]);

  const legacyInvitedOrg = useMemo(
    () =>
      legacyInviteOrgId
        ? organizations.find(
            org => org.id === legacyInviteOrgId && org.isInvited
          )
        : undefined,
    [legacyInviteOrgId, organizations]
  );

  // Token flow takes precedence and is mandatory (personal accounts are locked
  // out while an invite is open, so the user must accept or reject).
  if (inviteToken && tokenInfo && !dismissed) {
    // The invite belongs to a different identity — e.g. it was addressed to a
    // `user+tag@` subaddress, which Descope registers as its own account, while
    // the link was delivered to (and opened from) the base mailbox. Say which
    // account to use and let them dismiss, instead of showing Accept/Reject
    // that are guaranteed to 403 (KIR-4166).
    // Dismissing is local-only — it clears this browser's copy of the token and
    // the ?invite_token= param, and never touches the invite server-side, so the
    // emailed link still works for the address it was issued to.
    const keepsLinkValid =
      ' Dismissing this here only hides it on this account — the invitation stays valid.';
    const unavailableReason = tokenInfo.canRespond
      ? null
      : tokenInfo.invitedEmail
        ? `This invitation was sent to ${tokenInfo.invitedEmail}. Sign in with that account to accept or decline it.${keepsLinkValid}`
        : `This invitation was sent to a different account. Sign in with the invited address to accept or decline it.${keepsLinkValid}`;

    return (
      <InviteAcceptModal
        open
        onOpenChange={open => {
          if (!open) {
            clearPersistedInviteToken();
            // Also silence the org-list prompt for this org, or dismissing the
            // deep-link modal would immediately reopen the same invite through
            // the no-deep-link path below.
            dismissAutoInvite(tokenInfo.organizationId);
            setDismissed(true);
          }
        }}
        token={inviteToken}
        organizationId={tokenInfo.organizationId}
        organizationName={tokenInfo.organizationName}
        mandatory
        unavailableReason={unavailableReason}
      />
    );
  }

  if (legacyInvitedOrg && !dismissed) {
    return (
      <InviteAcceptModal
        open
        onOpenChange={open => {
          if (!open) {
            // Same reason as the token branch: keep the org-list prompt from
            // re-opening the invite the user just closed.
            dismissAutoInvite(legacyInvitedOrg.id);
            setDismissed(true);
          }
        }}
        organizationId={legacyInvitedOrg.id}
        organizationName={legacyInvitedOrg.name}
      />
    );
  }

  // No deep link in play: surface a pending invite straight from the user's own
  // org list, so an invitee who simply signs in still sees it (KIR-4166). Until
  // now the only automatic prompt was the `?invite_token=` link — an invitee who
  // lost the email, or who dismissed it once, had to know to hunt for the org in
  // the switcher.
  //
  // Only reached when there is no token and no legacy param, so it can never
  // flash over (or race) the deep-link modal while that one is still resolving.
  //
  // Deliberately NOT `mandatory`: this prompt is unsolicited, appears on an
  // ordinary login, and is driven purely by client state — exactly the shape
  // that turned into an inescapable page-blocker in this ticket. It must always
  // be closable, and closing it is remembered for the session only.
  // Gate on whether the deep-link query is still *settling*, not on whether a
  // token was present at mount. `inviteToken` / `legacyInviteOrgId` are captured
  // once and never cleared, so testing them directly would suppress this prompt
  // for the whole SPA session — even after the deep-link invite was dismissed or
  // turned out to be dead, hiding any OTHER pending invite the user has.
  //
  // Reaching this point already means neither deep-link branch rendered. The
  // only case left to protect is a token whose resolve is still in flight:
  // prompting now would flash the wrong modal before the real one arrives.
  // Read the query's own `isPending` rather than sniffing `data`/`error` for
  // `undefined`: TanStack Query v5 reports `error: null` (not `undefined`) while
  // a query is in flight, so an `=== undefined` check misreads "pending" as
  // "settled" and lets this prompt flash over the deep-link modal — and an
  // Escape aimed at that flash would session-dismiss an unrelated invite.
  // A disabled query is also `pending`, hence the token guard in front.
  const tokenStillResolving = Boolean(inviteToken) && tokenQueryPending;

  if (!tokenStillResolving) {
    const pendingInvite = organizations.find(
      org => org.isInvited && !autoDismissedOrgIds.includes(org.id)
    );
    if (pendingInvite) {
      return (
        <InviteAcceptModal
          open
          onOpenChange={open => {
            if (!open) {
              dismissAutoInvite(pendingInvite.id);
            }
          }}
          organizationId={pendingInvite.id}
          organizationName={pendingInvite.name}
        />
      );
    }
  }

  return null;
}
